/**
 * Copy and test ids for the competition futures card (specs/champions-league Story 15).
 *
 * The World Cup's own strings live in `FuturesCard.constants` and are reused where the wording
 * is genuinely shared ("Pick a team"). What differs is the vocabulary: a club competition has
 * a *winner*, not a *tournament winner*, and it locks at its own first kickoff rather than at
 * "the tournament".
 */

export const COMPETITION_FUTURES_COPY = {
  WINNER_LABEL: 'Winner',
  TOP_SCORER_LABEL: 'Top scorer',
  MODAL_WINNER: 'Who lifts it?',
  // Deliberately not the same string as TOP_SCORER_LABEL: the card header and the modal title
  // are both on screen at once, and two identical headings read as a rendering bug.
  MODAL_SCORER: 'Who wins the Golden Boot?',
  LOCKED_CAPTION: 'Locked, the competition has started',
} as const;

export const COMPETITION_FUTURES_TEST_IDS = {
  root: 'competition-futures',
  pick: (kind: 'winner' | 'scorer') => `competition-futures-${kind}`,
  locked: 'competition-futures-locked',
  /** The flag behind the kit, so a story can assert *which* flag (AC-15.11, AC-15.12). */
  plateFlag: (kind: 'winner' | 'scorer') => `competition-futures-${kind}-flag`,
  plateKit: (kind: 'winner' | 'scorer') => `competition-futures-${kind}-kit`,
  plateEmpty: 'competition-futures-winner-empty',
} as const;

/**
 * The club futures square (specs/champions-league AC-15.11).
 *
 * Geometry mirrors `FuturesCard.styles`'s `mainFlagPlate` so the two cards line up, but is
 * declared here rather than imported: that file exports a whole StyleSheet, not its numbers,
 * and AC-15.10 keeps it read-only.
 */
export const COMPETITION_FUTURES_PLATE = {
  WIDTH: { TIGHT: 78, NARROW: 90, DEFAULT: 102 },
  HEIGHT_RATIO: 0.66,
  /**
   * Share of the plate's height the kit is allowed to occupy. 0.84 rather than a rounder 0.8
   * for a reason: it puts the default-width kit at 64px, which is `SoccerKit`'s
   * `KIT_SHADOW_MIN_SIZE`. Below that the kit silently loses its ambient drop shadow — the one
   * thing separating it from a busy flag behind it, which is exactly the ground it now has.
   */
  KIT_HEIGHT_FRACTION: 0.84,
  SCRIM_DARK: 'rgba(0,0,0,0.38)',
  SCRIM_LIGHT: 'rgba(0,0,0,0.22)',
  /**
   * The ground when a club's country does not resolve, or when its flag fails to load
   * (AC-15.12). The UEFA flag: the product ships no generic "unknown country" asset, and this
   * card only renders on a European club competition today.
   */
  FALLBACK_COUNTRY_CODE: 'eu',
  /**
   * The UEFA asset is not shaped like the country flags. `eu.webp` is a 500×500 canvas holding
   * a 349×234 flag centred in transparent margins, where every country flag is a full-bleed 3:2
   * SVG. Dropped into the plate it renders as a small blue block floating in empty space.
   *
   * 500 / 349 is what the image has to be scaled to for its *flag* — not its canvas — to cover
   * the plate's width; the plate clips the rest. Measured off the asset, so it moves only if
   * the asset is replaced. Replace `eu.webp` with a full-bleed flag and this can go.
   */
  FALLBACK_FLAG_OVERSCAN: 500 / 349,
} as const;
